// The die behind a roll response window: what it is for, the face rolled (kept visible after
// the roll), and a 1–8 strip showing which results trigger what.
import * as uf from "../../../../common/unfathomable";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";

type Tone = "good" | "bad" | "none";
interface Face { tone: Tone; label?: string }

const kInteriorFaces = ["舰桥", "舰桥", "礼拜堂", "船长室", "货舱", "厨房", "锅炉房", "锅炉房"];

/** Losses for the ship read red; everything else a roll can trigger reads green. */
function toneOf(effect: string): Tone {
  if (/^无事/.test(effect.trim())) return "none";
  return /-\s?\d|损伤|弃|生成|激活|后退|被击败|沉/.test(effect) ? "bad" : "good";
}

/** Turn a rollDie hint (see common/unfathomable/rules) into one entry per final value 1..8. */
export function facesFor(hint: string | null): { faces: Face[]; map: boolean } {
  const faces: Face[] = Array.from({ length: 8 }, () => ({ tone: "none" as Tone }));
  if (!hint) return { faces, map: false };
  if (hint.startsWith("骰值对应室内")) return { faces: kInteriorFaces.map(label => ({ tone: "none", label })), map: true };
  if (hint.startsWith("骰值即甲板编号")) return { faces: faces.map((_, i) => ({ tone: "none", label: `甲板 ${i + 1}` })), map: true };
  if (hint.startsWith("1–4")) return { faces: faces.map((_, i) => ({ tone: "none", label: i < 4 ? "上舷前方" : "下舷前方" })), map: true };

  const clauses = hint.split("；");
  const matched = new Set<number>();
  let otherwise: string | null = null;
  for (const c of clauses) {
    const t = c.trim();
    let test: ((v: number) => boolean) | null = null;
    let effect = "";
    let m: RegExpMatchArray | null;
    if ((m = t.match(/^否则(.*)$/))) { otherwise = m[1]; continue; }
    if ((m = t.match(/^骰值\s*\+\s*\D*?(\d+)\s*≤\s*(\d+)：(.*)$/))) { const k = +m[2] - +m[1]; test = v => v <= k; effect = m[3]; }
    else if ((m = t.match(/^骰值\s*\+\s*\D*?(\d+)\s*≥\s*(\d+)：(.*)$/))) { const k = +m[2] - +m[1]; test = v => v >= k; effect = m[3]; }
    else if ((m = t.match(/^骰值\s*<\s*\D*?(\d+)：(.*)$/))) { const k = +m[1]; test = v => v < k; effect = m[2]; }
    else if ((m = t.match(/^≥\s*(\d+)\s*(?:：|则)\s*(.*)$/))) { const k = +m[1]; test = v => v >= k; effect = m[2]; }
    else if ((m = t.match(/^≤\s*(\d+)\s*(?:：|则)\s*(.*)$/))) { const k = +m[1]; test = v => v <= k; effect = m[2]; }
    else if ((m = t.match(/^≤\s*\D+?(\d+)：(.*)$/))) { const k = +m[1]; test = v => v <= k; effect = m[2]; }
    else if ((m = t.match(/^<\s*\D+?(\d+)：(.*)$/))) { const k = +m[1]; test = v => v < k; effect = m[2]; }
    if (!test) continue;
    const tone = toneOf(effect);
    for (let v = 1; v <= 8; v++) {
      if (matched.has(v) || !test(v)) continue;
      matched.add(v);
      faces[v - 1] = { tone, label: effect };
    }
  }
  if (otherwise !== null) {
    const tone = toneOf(otherwise);
    for (let v = 1; v <= 8; v++) if (!matched.has(v)) faces[v - 1] = { tone, label: otherwise };
  }
  return { faces, map: false };
}

function purposeOf(die: uf.DieInfo, map: boolean): { icon: string; label: string } {
  if (die.reason.includes("攻击")) return { icon: "⚔", label: "攻击" };
  if (map || die.reason.includes("位置")) return { icon: "🗺", label: "决定位置" };
  if (die.reason.includes("代价")) return { icon: "🩸", label: "代价" };
  return { icon: "🎲", label: "效果" };
}

export function DicePanel({ die }: { die: uf.DieInfo }) {
  const { faces, map } = facesFor(die.hint);
  const purpose = purposeOf(die, map);
  const after = die.phase === "after" && die.value !== null;
  const shown = after ? die.value! : die.setTo !== null ? Math.max(1, Math.min(8, die.setTo + die.modifier)) : null;
  const notes = (die.hint ?? "").split("；").filter(s => s && !/^≥\d+：无事$/.test(s));
  return (
    <div className={css.dicePanel}>
      <div className={css.diceHead}>
        <span className={css.dicePurpose}>{purpose.icon} {purpose.label}</span>
        <span className={css.diceReason}>{die.reason}</span>
      </div>
      <div className={css.diceBody}>
        <div className={css.diceFace} data-state={after ? "rolled" : "pending"}>
          <svg viewBox="-50 -50 100 100" width={74} height={74}>
            <polygon points="0,-46 33,-33 46,0 33,33 0,46 -33,33 -46,0 -33,-33" fill="url(#ufDieFace)" stroke="#3a2a14" strokeWidth={4} />
            <defs><linearGradient id="ufDieFace" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stopColor="#f6e7bf" /><stop offset="100%" stopColor="#9c7a43" /></linearGradient></defs>
            <text textAnchor="middle" dy={14} fontSize={40} fontWeight={700} fill="#2b1a08">{after ? die.raw : die.setTo ?? "?"}</text>
          </svg>
          <div className={css.diceMeta}>
            {die.modifier !== 0 && <div>修正 <b>{die.modifier > 0 ? "+" : ""}{die.modifier}</b></div>}
            {after && die.modifier !== 0 && <div>结果 <b>{die.value}</b></div>}
            {!after && die.setTo !== null && <div>已设定</div>}
            {!after && die.setTo === null && <div style={{ opacity: 0.7 }}>即将掷骰</div>}
          </div>
        </div>
        <div className={css.diceStrip}>
          {faces.map((f, i) => (
            <div key={i} className={css.diceCell} data-tone={f.tone} data-current={shown === i + 1 ? "1" : "0"} title={f.label}>
              <div className={css.diceNum}>{i + 1}</div>
              {map && <div className={css.diceLabel}>{f.label}</div>}
            </div>
          ))}
        </div>
      </div>
      {map && <div className={css.diceNotes}>骰值决定位置：{die.hint}</div>}
      {!map && notes.length > 0 && (
        <div className={css.diceNotes}>
          {notes.map((n, i) => <div key={i}><Rich text={n} /></div>)}
        </div>
      )}
    </div>
  );
}
